import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";

const API = "/api";

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors duration-300";

export default function SharedPage() {
  const { key } = useParams();
  const [code, setCode] = useState("");
  const [granted, setGranted] = useState("");
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const tryAccess = useCallback(
    async (value, silent = false) => {
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const resp = await fetch(`${API}/shared/${key}/access`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: value }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          if (!silent) setError(typeof data.detail === "string" ? data.detail : "Code d'accès incorrect");
          sessionStorage.removeItem(`shared-${key}`);
        } else {
          setPage(data);
          setGranted(value);
          sessionStorage.setItem(`shared-${key}`, value);
        }
      } catch {
        if (!silent) setError("Connexion impossible au serveur");
      }
      if (!silent) setLoading(false);
    },
    [key]
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Espace partagé — e².law";
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(robots);
    const saved = sessionStorage.getItem(`shared-${key}`);
    if (saved) tryAccess(saved, true);
    return () => {
      document.title = previousTitle;
      robots.remove();
    };
  }, [key, tryAccess]);

  const submit = (e) => {
    e.preventDefault();
    tryAccess(code);
  };

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="shared-gate">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="w-full max-w-md bg-ink-card border border-white/5 p-10"
          data-testid="shared-gate-form"
        >
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo-mark.png" alt="e².law" className="w-9 h-9" />
            <div>
              <p className="font-serif text-xl">Espace partagé</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">
                Contenu réservé aux participants
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="shared-code" className="label-accent block mb-3">Code d'accès</label>
            <input
              id="shared-code"
              type="text"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code d'accès transmis par mail"
              className={field}
              data-testid="shared-code-input"
            />
          </div>

          {error && (
            <p className="mt-6 text-xs text-red-400" data-testid="shared-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !code.trim()}
            data-testid="shared-code-submit"
            className="mt-10 w-full bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {loading ? "Vérification…" : "Accéder"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="px-6 py-24 md:py-32" data-testid="shared-content">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {page.subtitle && (
            <p className="label-accent mb-4" data-testid="shared-subtitle">{page.subtitle}</p>
          )}
          <h1 className="font-serif text-4xl sm:text-5xl mb-10" data-testid="shared-title">{page.title}</h1>

          {page.intro_html && (
            <div
              className="text-[#FAF8F5]/70 leading-relaxed space-y-4 mb-14 [&_a]:text-gold [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: page.intro_html }}
              data-testid="shared-intro"
            />
          )}

          {page.featured && (
            <a
              href={page.featured.url}
              className="block border border-gold bg-gold/5 p-8 mb-8 hover:bg-gold/10 transition-colors duration-300 group"
              data-testid="shared-featured"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">À lire en priorité</p>
              <p className="font-serif text-2xl mb-2 group-hover:text-gold transition-colors duration-300">
                {page.featured.title}
              </p>
              {page.featured.desc && (
                <p className="text-sm text-[#626773] leading-relaxed">{page.featured.desc}</p>
              )}
            </a>
          )}

          {page.file && (
            <a
              href={`${page.file.url}?code=${encodeURIComponent(granted)}`}
              className="block border border-gold/40 bg-ink-card p-8 mb-14 hover:border-gold transition-colors duration-300 group"
              data-testid="shared-download"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">Document de la séance</p>
              <p className="font-serif text-2xl mb-2 group-hover:text-gold transition-colors duration-300">
                {page.file.label}
              </p>
              <p className="text-xs text-[#626773]">PDF — téléchargement immédiat</p>
            </a>
          )}

          {page.sections && page.sections.length > 0
            ? page.sections.map((sec, si) => (
                <div key={sec.heading} className="mb-12" data-testid={`shared-section-${si}`}>
                  <p className="label-accent mb-6">{sec.heading}</p>
                  {sec.note && (
                    <p className="text-sm text-[#626773] italic -mt-3 mb-6" data-testid={`shared-section-note-${si}`}>
                      {sec.note}
                    </p>
                  )}
                  <div className="divide-y divide-white/5 border-y border-white/5">
                    {sec.links.map((l, i) => (
                      <a
                        key={l.url}
                        href={l.url}
                        className="flex items-start justify-between gap-6 py-5 group"
                        data-testid={`shared-link-${si}-${i}`}
                      >
                        <div>
                          <p className="font-serif text-lg group-hover:text-gold transition-colors duration-300">{l.title}</p>
                          {l.desc && <p className="text-sm text-[#626773] mt-1 leading-relaxed">{l.desc}</p>}
                        </div>
                        <span className="text-gold shrink-0 mt-1 transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </a>
                    ))}
                  </div>
                </div>
              ))
            : page.links && page.links.length > 0 && (
                <>
                  <p className="label-accent mb-6">Pour aller plus loin</p>
                  <div className="divide-y divide-white/5 border-y border-white/5" data-testid="shared-links">
                    {page.links.map((l, i) => (
                      <a
                        key={l.url}
                        href={l.url}
                        className="flex items-start justify-between gap-6 py-5 group"
                        data-testid={`shared-link-${i}`}
                      >
                        <div>
                          <p className="font-serif text-lg group-hover:text-gold transition-colors duration-300">{l.title}</p>
                          {l.desc && <p className="text-sm text-[#626773] mt-1 leading-relaxed">{l.desc}</p>}
                        </div>
                        <span className="text-gold shrink-0 mt-1 transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
        </motion.div>
      </div>
    </div>
  );
}
